import { useState, type ReactNode } from 'react';
import { Button, Field, Input, Textarea, Text, makeStyles } from '@fluentui/react-components';
import { tokens } from '@vigeo/ui';
import {
  CALENDAR_DATE, HR_EVIDENCE_UPLOAD_LIMITS, acceptedOffer, formatRecordedCalendarDate, offerTermsText, startOfDayInAgencyZone,
  type HrWorkflowRecord, type HrWorkflowView, type Person,
} from '@vigeo/shared';
import type { NewCommand, WorkflowRun } from './useHrWorkflow.js';
import { WithdrawOfferButton } from './WithdrawOfferButton.js';
import { StoredFileLink } from '@/features/files';
import { HrFileUpload, type HrUpload } from '@/features/uploads';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, paddingBlock: tokens.spacingVerticalM, minWidth: 0, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
  row: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  record: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, paddingBlock: tokens.spacingVerticalM, borderBottomWidth: tokens.strokeWidthThin, borderBottomStyle: 'solid', borderBottomColor: tokens.colorNeutralStroke2 },
  preserved: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
});

/**
 * Recording an offer signed outside the app. Its fields live here, so closing the form unmounts it
 * and the staged letter, date and note are gone with it.
 *
 * Clearing on close, not just hiding. `HrFileUpload` unmounts and forgets its staged file, but a
 * parent that kept the previous `HrUpload` showed an empty file field with the date and note still
 * filled, Record verification enabled, and filed the letter the user believed they had discarded.
 */
export function PaperOfferForm({ person, disabled, run, onDone, onCancel }: {
  readonly person: Person;
  readonly disabled: boolean;
  readonly run: WorkflowRun;
  /** Called once the verification is recorded. */
  readonly onDone?: () => void;
  /** When given, the form carries its own Cancel — it is the whole screen, not a toggle. */
  readonly onCancel?: () => void;
}) {
  const [letter, setLetter] = useState<HrUpload | null>(null);
  const [signedOn, setSignedOn] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  // RESOLVED HERE, NOT IN THE CLICK. Calling `startOfDayInAgencyZone` while building the command's
  // argument threw *outside* the promise, so the `.catch(() => {})` on it never applied and the
  // exception escaped into React. The button refuses instead.
  //
  // WHAT ACTUALLY THROWS, since an earlier version of this comment named two causes and both were
  // wrong. Not "a day that does not exist": a `type="date"` input runs the HTML value-sanitisation
  // algorithm, so `2026-02-30` never survives as a value at all. Not "a year outside four digits"
  // either: `CALENDAR_DATE` rejects those before the call. It is every year from 0001 to 1883 —
  // `America/New_York` used LMT (-04:56:02) until 1883-11-18 and the offset resolves in whole
  // minutes, so the round-trip check inside `startOfDayInAgencyZone` fails. A date input will hold
  // `0001-01-01` or `1500-06-15` quite happily. The behaviour was right; the explanation was not.
  const signedAtInstant = ((): string | null => {
    if (!CALENDAR_DATE.test(signedOn)) return null;
    try { return startOfDayInAgencyZone(signedOn); } catch { return null; }
  })();
  return <>
    <HrFileUpload label="Signed offer letter" required target={{ kind: 'evidence', personId: person.id, purpose: 'signed-offer' }}
      acceptedTypes={HR_EVIDENCE_UPLOAD_LIMITS.acceptedTypes} disabled={disabled}
      hint="Upload the signed original. Vigeo holds the letter itself, so there is no link to keep current."
      onChange={setLetter} />
    <Field label="Date signed" required><Input type="date" value={signedOn} onChange={(_, d) => setSignedOn(d.value)} /></Field>
    <Field label="What HR verified" required><Textarea value={evidenceNote} onChange={(_, d) => setEvidenceNote(d.value)} /></Field>
    {/* `letter.usable` mirrors the server's scan check so HR is not told to wait by a 409. The
        server re-checks it: a verdict can change between this render and the write. */}
    <Button disabled={disabled || letter === null || !letter.usable || signedAtInstant === null || !evidenceNote} onClick={() => {
      if (signedAtInstant === null || letter === null) return;
      void run({ action: 'record-existing-offer', uploadId: letter.uploadId, signedAt: signedAtInstant, note: evidenceNote })
        .then(() => { setLetter(null); setSignedOn(''); setEvidenceNote(''); onDone?.(); }).catch(() => {});
    }}>Record verification</Button>
    {onCancel === undefined ? null : <Button appearance="subtle" disabled={disabled} onClick={onCancel}>Cancel</Button>}
  </>;
}

/**
 * Every offer on the record as a list, with the buttons that were on Offer and stage before #440.
 *
 * Moved here whole. It is what Offer and stage still shows for the offer states whose frames are
 * not approved yet (sent and waiting, expired, withdrawn, paper-signed, sent back, several offers
 * at once), and what the full workflow panel shows. Each of those becomes its own screen when its
 * design lands; until then this keeps them working.
 */
export function LegacyOffers({ person, data, canEdit, disabled, run, handle, onDraftOffer, editor }: {
  readonly person: Person;
  readonly data: HrWorkflowView;
  readonly canEdit: boolean;
  readonly disabled: boolean;
  readonly run: WorkflowRun;
  readonly handle: (command: NewCommand) => void;
  /** Opens the offer editor on a new draft (null) or on this one. */
  readonly onDraftOffer: (record: Extract<HrWorkflowRecord, { kind: 'offer' }> | null) => void;
  /** The editor, when its owner has one open. Drawn where the old block drew it. */
  readonly editor?: ReactNode;
}) {
  const styles = useStyles();
  const [existing, setExisting] = useState(false);
  return <section className={styles.section} aria-label="Offers">
    <Text size={400} weight="semibold">Offers and signed terms</Text>
    {/*
      "Accepted", not "Accepted and filed". `acceptedOffer` is approval alone by design (ADR-011
      Q2 §4 — "onboarding does not wait on it"), so this line never knew whether the document was
      filed. Row 2.2 states filing per offer, where the signal is read.
    */}
    {data.records.filter((r) => r.kind === 'offer' || r.kind === 'existing-offer').map((record) => <div key={record.id} className={styles.record}>
      {record.kind === 'offer' ? <>
        <Text weight="semibold">{record.terms.position} — {acceptedOffer(record, data.offerSteps, data.candidacySince) ? 'Accepted' : record.status}</Text>
        <details><summary>Review exact terms</summary><div className={styles.preserved}>{offerTermsText(record.terms, record.candidateName)}</div></details>
        {record.reviewedAt ? <Text size={200}>Reviewed {new Date(record.reviewedAt).toLocaleString()} by {record.reviewedBy}</Text> : null}
        {record.instanceId ? <OfferProgress record={record} data={data} /> : null}
        {canEdit && record.status !== 'withdrawn' ? <div className={styles.row}>
          {record.status === 'draft' ? <>
            <Button disabled={disabled} onClick={() => onDraftOffer(record)}>Edit draft</Button>
            <Button disabled={disabled} onClick={() => handle({ action: 'review-offer', recordId: record.id, expectedRevision: record.revision })}>Confirm terms reviewed</Button>
          </> : null}
          {/* A LINK, not the command. Since #262 `issue-offer` also emails the candidate and
              mints their portal link, and a one-word button gave HR no sight of the message, the
              three pre-send checks or the delivery result. Row 1.4 is that screen. */}
          {record.status === 'reviewed' ? <Button appearance="primary" as="a" href={`/app/people/${encodeURIComponent(person.id)}/offer/send?record=${encodeURIComponent(record.id)}`}>Send offer</Button> : null}
          {record.status === 'draft' ? <Button as="a" href={`/app/people/${encodeURIComponent(person.id)}/offer/${encodeURIComponent(record.id)}/review`}>Review offer</Button> : null}
          {!data.offerSteps.some((s) => s.id === record.instanceId && (s.state === 'submitted' || s.state === 'approved')) ? <WithdrawOfferButton appearance="secondary" candidateName={`${person.name.givenName} ${person.name.familyName}`} disabled={disabled} onConfirm={() => handle({ action: 'withdraw-offer', recordId: record.id, expectedRevision: record.revision })} /> : null}
        </div> : null}
      </> : record.kind === 'existing-offer' ? <><Text weight="semibold">Existing signed offer verified</Text>
        {record.storedFileId === null && record.sourceUrl === null
          // Saying this out loud beats `StoredFileLink`'s silent null, which HR would read as a
          // permissions problem. The command cannot produce such a row; a hand-repaired one could.
          ? <Text size={200}>No signed letter is filed against this verification.</Text>
          : <StoredFileLink personId={person.id} storedFileId={record.storedFileId} legacyUrl={record.sourceUrl} label="Open filed signed offer"
              // The record names the file the `store-file` effect WILL create — the id is derived,
              // not observed — so between the write and the delivery there is nothing to open. The
              // default wording would tell HR they lack permission for their own delivery lag.
              whenMissing="The signed letter is still being filed. It will appear here shortly; if it does not, the upload did not complete." />}
        <Text>Signed {formatRecordedCalendarDate(record.signedAt)} — {record.note}</Text></> : null}
    </div>)}
    {canEdit ? <div className={styles.row}><Button disabled={disabled} onClick={() => onDraftOffer(null)}>Draft offer</Button><Button disabled={disabled} onClick={() => setExisting(!existing)}>Record existing signed offer</Button></div> : null}
    {editor}
    {existing && canEdit ? <PaperOfferForm person={person} disabled={disabled} run={run} onDone={() => setExisting(false)} /> : null}
    <Text size={200}>Issued offers appear in this person's secure portal. Open candidate portal access in Documents &amp; checklist to send the link. Signed offers are reviewed and filed through the review queue.</Text>
  </section>;
}

function OfferProgress({ record, data }: { record: Extract<HrWorkflowRecord, { kind: 'offer' }>; data: HrWorkflowView }) {
  const step = data.offerSteps.find((s) => s.id === record.instanceId);
  if (!step) return <Text>Offer preparation is incomplete. Contact HR support before sending another offer.</Text>;
  return <><Text>Candidate signature: {step.submittedAt ? `received ${new Date(step.submittedAt).toLocaleString()}` : 'pending'} · Review: {step.state}</Text><StoredFileLink personId={step.personId} storedFileId={step.storedFileId} legacyUrl={step.sharePointItemUrl} label="Open signed offer" /></>;
}
